<template>
  <div class="table-wrapper">
    <div class="table-header">
      <div class="table-header__title mb-0">
        <p class="text">
          You can configure CDN to respond to requests using a custom error page
          when your origin returns an HTTP 4xx or 5xx status code. For example,
          when your custom origin is unavailable and returning 5xx responses,
          CDN can return a static error page that is hosted on Amazon S3. You
          can also specify a minimum TTL to control how long CDN caches errors.
        </p>
      </div>
      <b-button
        v-if="$can('create', 'errorpages', 'cdn')"
        variant="bg-primary"
        to="#"
        class="mt-1"
        @click="onCreate()"
      >
        <GjIcon
          class="icon-left"
          name="Plus"
        />
        Create Custom Error Response
      </b-button>
      <b-button
        v-if="$can('update', 'errorpages', 'cdn')"
        variant="bg-white"
        class="mt-1 d-none d-lg-inline"
        :disabled="!isSelected"
        @click="onEditClick(selected[0].id)"
      >
        Edit
      </b-button>
      <button-loading
        v-if="$can('delete', 'errorpages', 'cdn')"
        variant="bg-white"
        class="mt-1 d-none d-lg-inline"
        :disabled="!isSelected"
        :spinning="removingErrorpage"
        @click="onDeleteClick(selected[0].id)"
      >
        Delete
      </button-loading>
    </div>
    <div class="default-table position-relative d-none d-lg-block">
      <b-table
        ref="table"
        :items="errorPages"
        :fields="fields"
        :class="{ loaded: !loading }"
        select-mode="single"
        show-empty
        selectable
        responsive
        @row-selected="onRowSelected"
      >
        <template #cell(selected)="{ rowSelected }">
          <select-button :selected="rowSelected" />
        </template>

        <template #empty>
          <div
            v-if="loading"
            class="loading-table text-center py-3"
          >
            <b-spinner variant="primary" />
          </div>
          <template v-else>
            <no-data
              no-data-text="No error pages have been added yet..."
              create-text="+ Create Error Page"
              access-page="Error Pages"
              :has-permissions="hasPermissions"
              @action="onCreate()"
            />
          </template>
        </template>
      </b-table>
    </div>
    <div class="position-relative d-lg-none mt-2">
      <div :class="{ 'opacity-7': loading || removingErrorpage }">
        <template v-for="errorpage in errorPages">
          <card
            :key="errorpage.id"
            :fields="fields"
            :item="errorpage"
            class="top-fade-in"
          >
            <template #options>
              <b-dropdown
                id="dropdown-1"
                right
                variant="white"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'errorpages', 'cdn')"
                  @click="onEditClick(errorpage.id)"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'errorpages', 'cdn')"
                  @click="onDeleteClick(errorpage.id)"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>
          </card>
        </template>
      </div>
      <div
        v-if="loading || removingErrorpage"
        class="mobile-loading"
      >
        <table-busy />
      </div>
      <template v-if="!loading && errorPages.length < 1">
        <paper-simple class="top-fade-in">
          <no-data
            class="text-center"
            no-data-text="No error pages have been added yet..."
            create-text="+ Create Error Page"
            access-page="Error Pages"
            :has-permissions="hasPermissions"
            @action="onCreate()"
          />
        </paper-simple>
      </template>
    </div>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'

export default {
  name: 'ErrorPages',
  components: {
    Card,
  },
  data() {
    return {
      selected: {},
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'httpErrorCode', isPrimary: true },
        'errorCachingMinimumTtl',
        'customizeErrorResponse',
        { key: 'httpResponseCode', isPrimary: true },
      ],
      errorpageId: null,
      hasPermissions: true,
    }
  },
  computed: {
    errorPages() {
      return this.$store.state.cdn.errorpages
    },
    loading() {
      return this.$store.state.cdn.fetchingErrorpages
    },
    removingErrorpage() {
      return this.$store.state.cdn.removingErrorpage
    },
    isSelected() {
      return this.selected[0]
    },
    row() {
      return this.selected[0] ? this.selected[0] : {}
    },
  },
  created() {
    this.id = this.$route.params.id
    this.fetchErrorPages()
  },
  methods: {
    onRowSelected(item) {
      this.selected = item
    },
    fetchErrorPages() {
      this.$store.dispatch('cdn/fetchErrorPages', this.id).catch(error => {
        if (error.response?.status === 403) {
          this.hasPermissions = false
          return
        }
        this.errorToast(
          error.response?.data?.errors[0]
            || 'Something went wrong while fetching error pages.',
        )
      })
    },
    onEditClick(errorpageId) {
      this.$router.push({
        name: 'distribution-error-pages-edit',
        params: { id: this.id, errorpageId },
      })
    },
    onCreate() {
      this.$router.push({
        name: 'distribution-error-pages-create',
        params: { id: this.id },
      })
    },
    onDeleteClick(errorpageId) {
      this.confirmDelete(
        'Delete Error Page',
        'Are you sure you want to delete this error page?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('cdn/removeErrorPage', {
              distributionId: this.id,
              id: errorpageId,
            })
            .then(() => {
              this.successToast('Error page was removed')
              this.selected = []
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
  },
}
</script>
